import { Alert, Box, Button, Typography } from '@mui/material';
import React from 'react';

class ThemeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Material-UI 테마 관련 오류 감지 (breakpoints, spacing, transitions, typography, palette)
    if (error?.message?.includes('breakpointsInput.up is not a function') ||
        error?.message?.includes('theme.spacing is not a function') ||
        error?.message?.includes('theme.transitions.create is not a function') ||
        error?.message?.includes('theme.typography.pxToRem is not a function') ||
        error?.message?.includes('theme.palette.getContrastText is not a function') ||
        error?.message?.includes('breakpoints') ||
        error?.message?.includes('spacing') ||
        error?.message?.includes('transitions') ||
        error?.message?.includes('pxToRem') ||
        error?.message?.includes('getContrastText') ||
        error?.stack?.includes('breakpoints.js') ||
        error?.stack?.includes('styleFunctionSx.js') ||
        error?.stack?.includes('createStyledComponent') ||
        error?.stack?.includes('IconButton') ||
        error?.stack?.includes('Button') ||
        error?.stack?.includes('Chip')) {
      return { 
        hasError: true,
        error,
        isBreakpointError: true,
      };
    }
    
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Material-UI 테마 오류인 경우 콘솔에 특별 메시지 출력
    if (error?.message?.includes('breakpointsInput.up is not a function') ||
        error?.message?.includes('theme.spacing is not a function') ||
        error?.message?.includes('theme.transitions.create is not a function') ||
        error?.message?.includes('theme.typography.pxToRem is not a function') ||
        error?.message?.includes('theme.palette.getContrastText is not a function')) {
      console.warn('🔧 Material-UI Theme Error Detected:', {
        error: error.message,
        stack: error.stack,
        component: this.props.componentName || 'Unknown',
        suggestion: 'Theme integrity check activated. Fallback theme methods applied.',
        errorType: error.message.includes('pxToRem') ? 'typography' : 
                   error.message.includes('getContrastText') ? 'palette' :
                   error.message.includes('spacing') ? 'spacing' : 
                   error.message.includes('transitions') ? 'transitions' : 'breakpoints',
      });
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      isBreakpointError: false,
    });
    
    // 페이지 새로고침으로 Material-UI 테마 초기화
    if (this.state.isBreakpointError) {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      const { componentName = '컴포넌트' } = this.props;
      
      if (this.state.isBreakpointError) {
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                🎨 테마 시스템 오류
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Material-UI 테마 처리 중 오류가 발생했습니다.
                <br />
                (typography, palette, spacing, transitions, breakpoints)
                <br />
                안전 모드로 전환되었습니다.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={this.handleRetry}
                size="small"
              >
                페이지 새로고침
              </Button>
            </Alert>
            
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                오류 세부사항: {this.state.error?.message || 'Material-UI breakpoint error'}
              </Typography>
            </Box>
          </Box>
        );
      }

      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="error">
            <Typography variant="h6" gutterBottom>
              {componentName} 오류
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              예기치 않은 오류가 발생했습니다.
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={this.handleRetry}
              size="small"
            >
              다시 시도
            </Button>
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ThemeErrorBoundary;